import { useState, useEffect } from "react";
import BrandLogo from "./components/common/BrandLogo";

const API_BASE = process.env.REACT_APP_API_URL || "https://go.brandhives.com";

// ─── Constants ────────────────────────────────────────────────────────────────
const GOLD     = "#0077B5";
const GOLD_DIM = "#005885";
const GOLD_DARK= "#EBF5FB";
const BG       = "#F3F2EF";
const CARD     = "#FFFFFF";
const BORDER   = "#E1E9EE";
const GREEN    = "#057642";

const MBTI_TYPES = [
  { type: "INTJ", label: "Architect"    },
  { type: "INTP", label: "Logician"     },
  { type: "ENTJ", label: "Commander"    },
  { type: "ENTP", label: "Debater"      },
  { type: "INFJ", label: "Advocate"     },
  { type: "INFP", label: "Mediator"     },
  { type: "ENFJ", label: "Protagonist"  },
  { type: "ENFP", label: "Campaigner"   },
  { type: "ISTJ", label: "Logistician"  },
  { type: "ISTP", label: "Virtuoso"     },
  { type: "ESTJ", label: "Executive"    },
  { type: "ESTP", label: "Entrepreneur" },
  { type: "ISFJ", label: "Defender"     },
  { type: "ISFP", label: "Adventurer"   },
  { type: "ESFJ", label: "Consul"       },
  { type: "ESFP", label: "Entertainer"  },
];

const MBTI_DESCS = {
  INTJ: "Strategic and precise — cuts straight to the structural insight or long-term implication",
  INTP: "Intellectually curious — highlights overlooked angles, paradoxes, and underlying principles",
  ENTJ: "Commanding and decisive — leads with the strategic takeaway or business implication",
  ENTP: "Sharp and provocative — challenges conventional wisdom and flips the narrative",
  INFJ: "Purpose-driven and insightful — connects topics to deeper human truth or pattern",
  INFP: "Warm and idealistic — frames topics through a human or emotional lens",
  ENFJ: "Charismatic and people-first — ties insight to how it impacts people or teams",
  ENFP: "Energetic and imaginative — highlights exciting possibilities and human connections",
  ISTJ: "Measured and factual — references specific data, facts, or proven methods",
  ISFJ: "Genuine and helpful — frames topics around how they serve or protect others",
  ESTJ: "Confident and structured — leads with the actionable insight or operational lesson",
  ESFJ: "Friendly and inclusive — connects topics to teamwork, culture, or shared values",
  ISTP: "Concise and pragmatic — focuses on how things work or what's mechanically interesting",
  ISFP: "Understated and authentic — finds the human or aesthetic angle in every topic",
  ESTP: "Bold and high-energy — gets to the point fast, leads with what happened and why it matters",
  ESFP: "Upbeat and relatable — makes topics feel alive and relevant to everyday experience",
};

// eslint-disable-next-line no-unused-vars
const MBTI_GROUPS = [
  { id: "analysts",  icon: "🧠", name: "Analysts",  sub: "Strategic · logical · independent", types: ["INTJ","INTP","ENTJ","ENTP"] },
  { id: "diplomats", icon: "💚", name: "Diplomats", sub: "Empathetic · purposeful · inspiring", types: ["INFJ","INFP","ENFJ","ENFP"] },
  { id: "sentinels", icon: "🏛️", name: "Sentinels", sub: "Reliable · organized · practical",   types: ["ISTJ","ISFJ","ESTJ","ESFJ"] },
  { id: "explorers", icon: "🌊", name: "Explorers", sub: "Spontaneous · bold · hands-on",      types: ["ISTP","ISFP","ESTP","ESFP"] },
];

// eslint-disable-next-line no-unused-vars
const TONE_COLORS = {
  professional:  { bg: "#EBF5FB", text: GOLD_DIM,  label: "Professional" },
  bold:          { bg: "#FEF3C7", text: "#92400E",  label: "Bold" },
  storytelling:  { bg: "#F0FAF4", text: "#057642",  label: "Storytelling" },
  conversational:{ bg: "#FDF2F8", text: "#9D174D",  label: "Conversational" },
};

const TONES = [
  { id: "professional",   label: "Professional",   fullDesc: "Polished and authoritative — a thought-leader voice that lands on a strategic insight." },
  { id: "conversational", label: "Conversational", fullDesc: "Warm and human, like texting a smart friend." },
  { id: "bold",           label: "Bold",           fullDesc: "Punchy and provocative. Short sentences, no hedging." },
  { id: "educational",    label: "Educational",    fullDesc: "Clear and value-first — teaches one thing and shows why it matters." },
  { id: "storytelling",   label: "Storytelling",   fullDesc: "Narrative and personal. Draws readers in with a human scene." },
];

const CATEGORIES = [
  {
    id: "world_news", label: "World News", icon: "🌎", sub: "World Headlines",
    subtopics: [{ label: "World Headlines", slugs: ["spanglishreporter.substack.com/p"] }],
  },
  {
    id: "hispanic_us", label: "US Regional News", icon: "🇺🇸", sub: "US News · San Jose · El Paso · Houston",
    subtopics: [
      { label: "US News",    slugs: ["mercadonews.com/blogs/national", "spanglishusa.substack.com/p", "spanglish.ca/blogs/world-page/united-states"] },
      { label: "San Jose",   slugs: ["mercadonews.com/blogs/national/san-jose", "spanglish.ca/blogs/world-page/united-states/san-jose"] },
      { label: "El Paso",    slugs: ["mercadonews.com/blogs/national/el-paso", "spanglish.ca/blogs/world-page/united-states/el-paso"] },
      { label: "Houston",    slugs: ["spanglish.ca/blogs/world-page/united-states/houston"] },
      { label: "East Coast", slugs: ["spanglish.ca/blogs/world-page/united-states/east-coast"] },
    ],
  },
  {
    id: "canadian_regional", label: "Canadian Regional News", icon: "🍁", sub: "Canada · Toronto · BC · Montreal",
    subtopics: [
      { label: "Canada",   slugs: ["spanglishcanada.substack.com/p"] },
      { label: "Toronto",  slugs: ["spanglish.ca/blogs/world-page/canada-page/toronto"] },
      { label: "BC",       slugs: ["spanglish.ca/blogs/world-page/canada-page/bc"] },
      { label: "Montreal", slugs: ["spanglish.ca/blogs/world-page/canada-page/montreal"] },
      { label: "Calgary",  slugs: ["spanglish.ca/blogs/world-page/calgary"] },
    ],
  },
  {
    id: "canada_espanol", label: "Canada en Español", icon: "🇨🇦", sub: "Toronto · Calgary · BC · Montreal",
    subtopics: [
      { label: "Toronto",                    slugs: ["hispanosencanada.ca/blogs/canada/toronto"] },
      { label: "Calgary",                    slugs: ["hispanosencanada.ca/blogs/canada/calgary"] },
      { label: "British Columbia",           slugs: ["hispanosencanada.ca/blogs/canada/columbia-britanica"] },
      { label: "Montreal",                   slugs: ["hispanosencanada.ca/blogs/canada/montreal"] },
      { label: "New Brunswick & East Coast", slugs: ["hispanosencanada.ca/blogs/canada/costa-este"] },
      { label: "Vida Activa",                slugs: ["hispanosencanada.ca/blogs/rumbo"] },
      { label: "Negocio Propio",             slugs: ["hispanosencanada.ca/blogs/impulso"] },
    ],
  },
  {
    id: "real_estate", label: "Real Estate & Housing", icon: "🏠", sub: "Housing · Investment · Retirement",
    subtopics: [
      { label: "Real Estate",          slugs: ["micasita.ca/blogs/real-estate"] },
      { label: "Real Estate Insights", slugs: ["micasita.ca/blogs/real-estate-insights"] },
      { label: "Retirement Taxes",     slugs: ["micasita.ca/blogs/retirement-taxes"] },
      { label: "Retirement Homes",     slugs: ["micasita.ca/blogs/retirement-homes"] },
      { label: "Rental Property",      slugs: ["micasita.ca/blogs/rental-property"] },
      { label: "Retire Abroad",        slugs: ["micasita.ca/blogs/retire-abroad"] },
      { label: "Home Price Trends",    slugs: ["micasita.ca/blogs/home-price-trends"] },
      { label: "Housing News",         slugs: ["micasita.ca/blogs/housing-news"] },
      { label: "Los Angeles",          slugs: ["micasita.ca/blogs/los-angeles"] },
      { label: "San Jose",             slugs: ["micasita.ca/blogs/san-jose"] },
      { label: "Houston",              slugs: ["micasita.ca/blogs/houston"] },
      { label: "New York",             slugs: ["micasita.ca/blogs/new-york"] },
      { label: "New Orleans",          slugs: ["micasita.ca/blogs/new-orleans"] },
      { label: "Chicago",              slugs: ["micasita.ca/blogs/chicago"] },
      { label: "San Antonio",          slugs: ["micasita.ca/blogs/san-antonio"] },
      { label: "San Diego",            slugs: ["micasita.ca/blogs/san-diego"] },
      { label: "Dallas",               slugs: ["micasita.ca/blogs/dallas"] },
      { label: "El Paso",              slugs: ["micasita.ca/blogs/el-paso"] },
      { label: "Calgary",              slugs: ["micasita.ca/blogs/calgary"] },
      { label: "Montreal",             slugs: ["micasita.ca/blogs/montreal"] },
      { label: "Toronto",              slugs: ["micasita.ca/blogs/toronto"] },
      { label: "BC",                   slugs: ["micasita.ca/blogs/bc"] },
      { label: "Canada",               slugs: ["micasita.ca/blogs/canada"] },
      { label: "New Brunswick",        slugs: ["micasita.ca/blogs/new-brunswick"] },
      { label: "Real Estate News",     slugs: ["spanglishrealestate.substack.com/p"] },
    ],
  },
  {
    id: "legal", label: "Immigration & Law", icon: "⚖️", sub: "Immigration · Family · Business Law",
    subtopics: [
      { label: "US Immigration",     slugs: ["cozlaw.com/blogs/us-immigration-help"] },
      { label: "US Injury Law",      slugs: ["cozlaw.com/blogs/us-injury-law"] },
      { label: "US Family Law",      slugs: ["cozlaw.com/blogs/us-family-law"] },
      { label: "US Criminal Defense",slugs: ["cozlaw.com/blogs/us-criminal-defense"] },
      { label: "US Business Law",    slugs: ["cozlaw.com/blogs/us-business-law"] },
      { label: "Canada Immigration", slugs: ["cozlaw.com/blogs/canada-immigration-help"] },
      { label: "Canada Family Law",  slugs: ["cozlaw.com/blogs/canada-family-law"] },
      { label: "Canada Injury Law",  slugs: ["cozlaw.com/blogs/canada-injury-law"] },
      { label: "Canada Business Law",slugs: ["cozlaw.com/blogs/canada-business-law"] },
    ],
  },
  {
    id: "business", label: "Business & Entrepreneurship", icon: "📈", sub: "Startups · Founders · Growth",
    subtopics: [
      { label: "Founder Stories",      slugs: ["biz-index.net/blogs/founder-stories"] },
      { label: "SaaS Tools",           slugs: ["biz-index.net/blogs/saas-tools"] },
      { label: "Startup Scene",        slugs: ["biz-index.net/blogs/startup-scene"] },
      { label: "Startup & Franchise",  slugs: ["biz-index.net/blogs/startup-and-franchise"] },
      { label: "Startup News",         slugs: ["biz-index.net/blogs/startup-news"] },
      { label: "Small Business Growth",slugs: ["biz-index.net/blogs/small-business-growth"] },
      { label: "Entrepreneur Tips",    slugs: ["biz-index.net/blogs/entrepreneur-tips"] },
      { label: "Pitch Events",         slugs: ["biz-index.net/blogs/pitch-events"] },
      { label: "Legal for Small Biz",  slugs: ["biz-index.net/blogs/legal-for-small-biz"] },
      { label: "Business Insurance",   slugs: ["biz-index.net/blogs/business-insurance"] },
      { label: "Tax & Accounting",     slugs: ["biz-index.net/blogs/tax-and-accounting"] },
      { label: "Insider Trades",       slugs: ["spanglishinsidertrades.substack.com/p"] },
      { label: "Tech Startups",        slugs: ["spanglishtechstartup.substack.com/p"] },
      { label: "Business (Substack)",  slugs: ["spanglishbusiness.substack.com/p"] },
      { label: "Markets",              slugs: ["mercadonews.com/blogs/market"] },
      { label: "Economy",              slugs: ["mercadonews.com/blogs/economy"] },
      { label: "Entrepreneurship",     slugs: ["mercadonews.com/blogs/playbook"] },
      { label: "Money",                slugs: ["mercadonews.com/blogs/money"] },
    ],
  },
  {
    id: "health_wellness", label: "Health & Wellness", icon: "🧠", sub: "Mental Health · Research · Gut Health",
    subtopics: [
      { label: "Gut Health",       slugs: ["daymed.com/blogs/gut-health"] },
      { label: "Chronic Illness",  slugs: ["daymed.com/blogs/chronic-illness"] },
      { label: "Disease Outbreaks",slugs: ["daymed.com/blogs/disease-outbreaks"] },
      { label: "Sleep & Recovery", slugs: ["daymed.com/blogs/sleep-and-recovery"] },
      { label: "Health Insurance", slugs: ["daymed.com/blogs/health-insurance"] },
      { label: "Medical Research", slugs: ["daymed.com/blogs/medical-research"] },
      { label: "Mental Health",    slugs: ["daymed.com/blogs/mental-health"] },
    ],
  },
  {
    id: "lifestyle", label: "Food & Lifestyle", icon: "🌿", sub: "Recipes · Wellness · Culture · Soccer",
    subtopics: [
      { label: "Natural Remedies",  slugs: ["eat9.ca/blogs/natural-remedies"] },
      { label: "Clean Eating",      slugs: ["eat9.ca/blogs/clean-eating"] },
      { label: "Healthy Recipes",   slugs: ["eat9.ca/blogs/healthy-recipes"] },
      { label: "Detox & Gut Health",slugs: ["eat9.ca/blogs/detox-and-gut-health"] },
      { label: "Green Products",    slugs: ["eat9.ca/blogs/green-products"] },
      { label: "Organic Food",      slugs: ["eat9.ca/blogs/organic-food"] },
      { label: "Sustainable Living",slugs: ["eat9.ca/blogs/sustainable-living"] },
      { label: "Culture",           slugs: ["spanglishentertainment.substack.com/p"] },
      { label: "Soccer",            slugs: ["spanglishsoccer.substack.com/p"] },
      { label: "Living Abroad",     slugs: ["mercadonews.com/blogs/abroad"] },
    ],
  },
];

const SLUG_MAP = {};
CATEGORIES.forEach(cat => {
  cat.subtopics.forEach(sub => {
    SLUG_MAP[`${cat.id}::${sub.label}`] = sub.slugs;
  });
});

const DAYS_OF_WEEK = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
const HOURS = Array.from({ length: 14 }, (_, i) => i + 5);
const TIMEZONES = [
  { id: "America/Toronto",     label: "Eastern (ET)",          offset: "UTC-5/4"   },
  { id: "America/Chicago",     label: "Central (CT)",          offset: "UTC-6/5"   },
  { id: "America/Denver",      label: "Mountain (MT)",         offset: "UTC-7/6"   },
  { id: "America/Los_Angeles", label: "Pacific (PT)",          offset: "UTC-8/7"   },
  { id: "America/Halifax",     label: "Atlantic (AT)",         offset: "UTC-4/3"   },
  { id: "America/Winnipeg",    label: "Central Canada (CT)",   offset: "UTC-6/5"   },
  { id: "America/Edmonton",    label: "Mountain Canada (MT)",  offset: "UTC-7/6"   },
  { id: "America/Sao_Paulo",   label: "Brazil (BRT)",          offset: "UTC-3"     },
  { id: "America/Mexico_City", label: "Mexico (CT)",           offset: "UTC-6/5"   },
  { id: "Europe/Paris",        label: "Europe (CET)",          offset: "UTC+1/2"   },
  { id: "Australia/Sydney",    label: "Australia East (AEST)", offset: "UTC+10/11" },
];

const formatHour = h => h === 12 ? "12:00 pm" : h < 12 ? `${h}:00 am` : `${h-12}:00 pm`;

// Progress bar labels (steps 1–4 in the 6-step flow: 0-1-2-3-4-5)
const PROGRESS_LABELS_STANDARD = ["Connect LinkedIn", "Your voice", "Your topics", "Your details"];
const PROGRESS_LABELS_PRO = ["Connect LinkedIn", "Your voice", "Your profession", "Practice areas", "Your details"];

// ─── Busy Bee banner ──────────────────────────────────────────────────────────
function BusyBee({ onRetry }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#FFFBEB", border: "1px solid #FCD34D", borderRadius: 10, padding: "12px 18px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, color: "#92400E", margin: "12px 0" }}>
      <span style={{ fontSize: 20 }}>🐝</span>
      <span style={{ flex: 1 }}>Our bees are a little busy right now — hang tight and try again in a moment.</span>
      {onRetry && (
        <button onClick={onRetry} style={{ background: "#F59E0B", color: "#fff", border: "none", borderRadius: 6, padding: "6px 14px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
          Try Again
        </button>
      )}
    </div>
  );
}

// ─── Legal Modal ──────────────────────────────────────────────────────────────
function LegalModal({ type, onClose }) {
  const H = { fontFamily: "'Playfair Display',Georgia,serif", fontSize: 17, fontWeight: 700, color: "#1A1A1A", marginTop: 28, marginBottom: 8, borderBottom: "1px solid #E1E9EE", paddingBottom: 6 };
  const P = { fontSize: 14, lineHeight: 1.75, color: "#333", marginBottom: 14 };
  const UL = { fontSize: 14, lineHeight: 1.75, color: "#333", marginBottom: 14, paddingLeft: 22 };
  const LK = { color: "#0077B5" };
  const termsContent = (
    <>
      <p style={P}>Last updated: March 31, 2026</p>
      <p style={P}>These Terms of Use ("Terms") govern your use of BrandHives, a LinkedIn content automation service operated by BHIVE Social Media Labs ("BHIVE", "we", "us"). By creating an account you agree to these Terms.</p>
      <h2 style={H}>1. The Service</h2>
      <p style={P}>BrandHives automatically generates and publishes LinkedIn posts on your behalf using artificial intelligence. You authorize BrandHives to connect to your LinkedIn account and post content according to your selected schedule and preferences.</p>
      <h2 style={H}>2. Your Account</h2>
      <ul style={UL}><li>You must be 16 or older to use Nectar.</li><li>You are responsible for maintaining the security of your account credentials.</li><li>You must provide accurate information during registration.</li><li>One account per person. You may not share accounts or create accounts on behalf of others without their explicit consent.</li></ul>
      <h2 style={H}>3. AI-Generated Content</h2>
      <ul style={UL}><li>All content generated by Nectar is produced by artificial intelligence.</li><li>You are solely responsible for reviewing, approving, and publishing all content to your LinkedIn profile.</li><li>BHIVE does not guarantee the accuracy, completeness, or appropriateness of any generated content.</li><li>Licensed professionals (lawyers, accountants, financial advisors) are solely responsible for ensuring all published content complies with their applicable professional and regulatory obligations.</li></ul>
      <h2 style={H}>4. LinkedIn Authorization</h2>
      <p style={P}>By connecting your LinkedIn account you authorize BHIVE to read your basic profile information, publish posts to your LinkedIn profile on your behalf, and access your LinkedIn follower count. You can revoke this authorization at any time through your LinkedIn account settings or BrandHives Settings.</p>
      <h2 style={H}>5. Subscription and Billing</h2>
      <ul style={UL}><li>BrandHives is offered on a subscription basis. Pricing is displayed at signup and may change with 30 days notice.</li><li>Your subscription renews automatically unless cancelled.</li><li>Refunds are not provided for partial billing periods.</li><li>We reserve the right to suspend accounts with failed payments.</li></ul>
      <h2 style={H}>6. Acceptable Use</h2>
      <p style={P}>You may not use BrandHives to post content that violates LinkedIn's Terms of Service, publish spam or defamatory content, impersonate any person or organization, violate any applicable law, or attempt to reverse engineer any part of the service. BHIVE reserves the right to suspend or terminate accounts that violate these rules without refund.</p>
      <h2 style={H}>7. Intellectual Property</h2>
      <ul style={UL}><li>You retain ownership of your LinkedIn profile and any content you create or approve through BrandHives.</li><li>You grant BHIVE a limited license to generate, store, and publish content on your behalf as directed by your account settings.</li><li>BHIVE retains all rights to the BrandHives platform, software, and underlying technology.</li></ul>
      <h2 style={H}>8. Disclaimer of Warranties</h2>
      <p style={P}>BrandHives is provided "as is" without warranty of any kind. BHIVE does not warrant that the service will be uninterrupted, error-free, or that generated content will meet your specific requirements.</p>
      <h2 style={H}>9. Limitation of Liability</h2>
      <p style={P}>To the maximum extent permitted by applicable law, BHIVE's total liability for any claims arising from your use of BrandHives shall not exceed the amount you paid to BHIVE in the 1 month preceding the claim. BHIVE is not liable for any indirect, incidental, or consequential damages.</p>
      <h2 style={H}>10. Termination</h2>
      <ul style={UL}><li>You may cancel your account at any time through Settings → Account.</li><li>Cancellation stops all future posts and billing at the end of the current billing period.</li><li>BHIVE may terminate your account for violation of these Terms with or without notice.</li></ul>
      <h2 style={H}>11. Governing Law</h2>
      <p style={P}>These Terms are governed by the laws of the Province of Ontario and the federal laws of Canada applicable therein. Any disputes shall be resolved in the courts of Toronto, Ontario.</p>
      <h2 style={H}>12. Changes to These Terms</h2>
      <p style={P}>We may update these Terms at any time. We will notify you by email at least 14 days before material changes take effect. Continued use of BrandHives after changes take effect constitutes acceptance of the new Terms.</p>
      <h2 style={H}>13. Contact</h2>
      <p style={P}>BHIVE Social Media Labs · 65 Jefferson Avenue, Toronto, ON · <a href="mailto:operator@bhive.ca" style={LK}>operator@bhive.ca</a></p>
    </>
  );
  const privacyContent = (
    <>
      <p style={P}>Last updated: March 22, 2026</p>
      <p style={P}>BHIVE™ Social Media Labs ("BHIVE™," "we," "us," or "our") provides this Privacy Policy to inform you of our policies regarding the collection, use, protection, and disclosure of Personal Information received from your use of the BrandHives platform.</p>
      <h2 style={H}>1. Personal Information We Collect</h2>
      <p style={P}>We collect information you provide directly (name, email, payment details), information from your connected LinkedIn account (profile data, OAuth tokens), and information automatically collected through your use of the service (log data, cookies).</p>
      <h2 style={H}>2. LinkedIn Data</h2>
      <p style={P}>When you connect your LinkedIn account, we collect your LinkedIn member ID, name, profile picture, headline, follower count, and OAuth access tokens. This data is used exclusively to generate and publish LinkedIn posts on your behalf. We do not sell or share LinkedIn member data for advertising purposes. OAuth tokens are encrypted at rest and deleted within 30 days of account cancellation.</p>
      <h2 style={H}>3. AI-Generated Content</h2>
      <p style={P}>BrandHives uses AI, including large language models provided by OpenAI, to generate social media content on your behalf. AI systems can make mistakes. You are solely responsible for reviewing and taking accountability for any content published to your LinkedIn profile. If you are a licensed professional, you are solely responsible for ensuring content complies with your applicable professional and regulatory obligations.</p>
      <h2 style={H}>4. How We Use Your Information</h2>
      <ul style={UL}><li>Create and manage your account and provide the Service</li><li>Generate and publish AI-assisted social media content on your behalf</li><li>Communicate with you for account management, notifications, and service updates</li><li>Process payments and manage subscriptions</li><li>Improve and operate our business</li><li>Comply with applicable legal requirements</li></ul>
      <h2 style={H}>5. How We Share Your Information</h2>
      <p style={P}>We share your information only with trusted service providers necessary to operate the service: Stripe (payments), Supabase (database), OpenAI (AI content), Emailit (email), Hetzner (hosting), and LinkedIn (publishing). We do not sell your personal information.</p>
      <h2 style={H}>6. Data Security</h2>
      <p style={P}>We use encryption for OAuth tokens at rest, encrypted database connections, and row-level security on sensitive data tables. No security measures are perfect or impenetrable.</p>
      <h2 style={H}>7. Your Rights</h2>
      <p style={P}>You may access, correct, or request deletion of your personal information by contacting us at <a href="mailto:operator@bhive.ca" style={LK}>operator@bhive.ca</a>. You may cancel your account at any time through Settings → Account. Unsubscribing from emails will stop all automated posts and cancel your subscription.</p>
      <h2 style={H}>8. Data Retention</h2>
      <p style={P}>We retain your data for as long as your account is active. Upon cancellation, LinkedIn tokens are deleted within 30 days, profile data is anonymized or deleted within 90 days, and your email is retained to prevent duplicate free trial registrations.</p>
      <h2 style={H}>9. Children's Privacy</h2>
      <p style={P}>The Service is not directed to persons under 16. We do not knowingly collect Personal Information from children under 16.</p>
      <h2 style={H}>10. Contact Us</h2>
      <p style={P}>BHIVE™ Social Media Labs · 65 Jefferson Avenue, Toronto, ON, Canada · <a href="mailto:operator@bhive.ca" style={LK}>operator@bhive.ca</a></p>
    </>
  );
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 2000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "5vh 16px", overflowY: "auto" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 560, marginBottom: 40 }}>
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #E1E9EE", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 20, fontWeight: 700 }}>{type === "terms" ? "Terms of Use" : "Privacy Policy"}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 24, color: "#999", lineHeight: 1, padding: "0 0 2px 12px" }}>×</button>
        </div>
        <div style={{ padding: "4px 24px 24px", maxHeight: "65vh", overflowY: "auto" }}>
          {type === "terms" ? termsContent : privacyContent}
        </div>
        <div style={{ borderTop: "1px solid #E1E9EE", padding: "16px 24px" }}>
          <button onClick={onClose} style={{ width: "100%", background: "#0077B5", color: "#fff", border: "none", borderRadius: 8, padding: "12px", fontSize: 15, cursor: "pointer", fontFamily: "'Playfair Display',Georgia,serif" }}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function NectarOnboard({ onComplete, onSignIn }) {
  const [step, setStep] = useState(() => {
    const p = new URLSearchParams(window.location.hash.split("?")[1] || "");
    return p.get("step") === "1" ? 1 : 0;
  });

  // LinkedIn identity (from session after OAuth)
  const [linkedinName, setLinkedinName] = useState(null);
  const [linkedinProfileImage, setLinkedinProfileImage] = useState(null); // eslint-disable-line no-unused-vars

  // Voice / MBTI
  const [voicePath, setVoicePath] = useState(null); // "dropdown" | "groups" | "tone"
  const [activeGroup, setActiveGroup] = useState(null); // eslint-disable-line no-unused-vars
  const [mbtiType, setMbtiType] = useState(null);
  const [tone, setTone] = useState(null);
  // MBTI quiz (Path 2)
  const [quizStep, setQuizStep] = useState(0); // 0-3: questions, 4: result
  const [quizAnswers, setQuizAnswers] = useState({}); // { ei: "E"|"I", sn: "S"|"N", tf: "T"|"F", jp: "J"|"P" }

  // Topics
  const [topicSelection, setTopicSelection] = useState({});
  const [selectedTopicIds, setSelectedTopicIds] = useState([]);
  const [expandedTopic, setExpandedTopic] = useState(null);
  const [topicWarning, setTopicWarning] = useState(false);

  // ── Pro — profession onboarding (steps 6 + 7, unused numbers) ──
  // Step integers are equality checks, not an ordered list. Sequence is
  // controlled by setStep() calls. Steps 6/7 take unused numbers so nothing
  // existing moves and the useEffect at line 412 (step 4 guard) is untouched.
  const STEP_PROFESSION = 6;
  const STEP_PRACTICE_AREAS = 7;
  const [proEnabled, setProEnabled] = useState(false);

  // Fetch Pro flag from server on mount — not baked in at build time
  useEffect(() => {
    fetch(`${API_BASE}/app/api/onboard/config`)
      .then(r => r.ok ? r.json() : {})
      .then(d => { if (d.authority_pro_enabled) setProEnabled(true); })
      .catch(() => {});
  }, []);

  const PROFESSIONS = [
    { id: "lawyer", label: "Lawyer", has_curated: true },
    { id: "accountant", label: "Accountant", has_curated: true },
    { id: "financial_advisor", label: "Financial Advisor", has_curated: true },
    { id: "real_estate", label: "Real Estate Agent", has_curated: true },
    { id: "mortgage_broker", label: "Mortgage Broker", has_curated: true },
    { id: "insurance_advisor", label: "Insurance Advisor (P&C)", has_curated: true },
    { id: "cybersecurity", label: "Cybersecurity Professional", has_curated: true },
    { id: "ai_professional", label: "AI / Machine Learning Professional", has_curated: true },
    { id: "hr", label: "HR Professional", has_curated: true },
    { id: "consultant", label: "Consultant", has_curated: false },
    { id: "coach", label: "Coach", has_curated: false },
  ];
  const JURISDICTIONS = [
    { id: "canada", label: "Canada" },
    { id: "united_states", label: "United States" },
    { id: "other", label: "Somewhere else" },
  ];
  const PRACTICE_AREA_MAP = {
    lawyer: [
      { id: "legal_litigation", label: "Litigation" },
      { id: "legal_ediscovery", label: "eDiscovery" },
      { id: "legal_gov_investigations", label: "Government Investigations" },
      { id: "legal_regulatory", label: "Regulatory and Enforcement" },
      { id: "legal_cross_border", label: "Cross-Border Legal" },
      { id: "legal_corporate_commercial", label: "Corporate and Commercial" },
      { id: "legal_ip", label: "Intellectual Property" },
      { id: "legal_labour_employment", label: "Labour and Employment" },
      { id: "legal_privacy_data", label: "Privacy and Data" },
      { id: "legal_tax", label: "Tax Law" },
      { id: "legal_securities", label: "Securities and Capital Markets" },
      { id: "legal_insolvency", label: "Insolvency and Restructuring" },
      { id: "legal_commercial_real_estate", label: "Commercial Real Estate" },
      { id: "legal_adr", label: "Arbitration and ADR" },
    ],
    mortgage_broker: [
      { id: "mortgage_rates", label: "Rates and Monetary Policy" },
      { id: "mortgage_regulation", label: "Lending Rules and Regulation" },
      { id: "mortgage_industry", label: "Industry and Brokerage" },
    ],
    insurance_advisor: [
      { id: "ins_claims_coverage", label: "Claims and Coverage Disputes" },
      { id: "ins_commercial_pc", label: "Commercial P&C" },
      { id: "ins_cyber_tech", label: "Cyber and Technology" },
      { id: "ins_catastrophe", label: "Catastrophe and Climate" },
      { id: "ins_industry", label: "Industry and M&A" },
    ],
    accountant: [
      { id: "acct_tax", label: "Tax" },
      { id: "acct_audit_assurance", label: "Audit and Assurance" },
      { id: "acct_financial_reporting", label: "Financial Reporting" },
    ],
    financial_advisor: [
      { id: "ins_life_annuity", label: "Life and Annuity Products" },
      { id: "fin_retirement_planning", label: "Retirement and Estate Planning" },
      { id: "fin_advisor_regulation", label: "Advisor Regulation" },
    ],
    cybersecurity: [
      { id: "cyber_threats_incidents", label: "Threats and Incidents" },
      { id: "cyber_ops_defense", label: "Defense and Operations" },
      { id: "cyber_governance_compliance", label: "Governance and Compliance" },
    ],
    ai_professional: [
      { id: "ai_governance_risk", label: "AI Governance and Risk" },
      { id: "ai_engineering_practice", label: "AI Engineering and Practice" },
    ],
    real_estate: [
      { id: "re_us_market", label: "Housing Market and Trends (US)" },
      { id: "re_us_industry", label: "Industry and Regulation (US)" },
      { id: "re_ca_market", label: "Housing Market and Trends (CA)" },
      { id: "re_ca_industry", label: "Industry and Regulation (CA)" },
    ],
    hr: [
      { id: "hr_talent_recruiting", label: "Talent and Recruiting" },
      { id: "hr_workplace_culture", label: "Workplace Culture" },
      { id: "hr_comp_benefits", label: "Compensation and Benefits" },
      { id: "hr_tech_ops", label: "HR Tech and Operations" },
      { id: "hr_compliance_ca", label: "HR Compliance (Canada)" },
      { id: "hr_compliance_us", label: "HR Compliance (US)" },
    ],
  };

  const [userProfession, setUserProfession] = useState(null);
  const [userJurisdiction, setUserJurisdiction] = useState(null);
  const [selectedPracticeAreas, setSelectedPracticeAreas] = useState([]);

  const proQualified = proEnabled
    && userProfession && userProfession !== "other"
    && PROFESSIONS.find(p => p.id === userProfession)?.has_curated
    && userJurisdiction && userJurisdiction !== "other";

  // Step 5 — Login details
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [emailConfirm, setEmailConfirm] = useState("");
  const [mode, setMode] = useState(null);
  const [schedule, setSchedule] = useState({ day: null, hour: null, timezone: (() => { try { const tz = Intl.DateTimeFormat().resolvedOptions().timeZone; const TZ_ALIAS = {"America/Vancouver":"America/Los_Angeles","America/New_York":"America/Toronto","America/Phoenix":"America/Denver"}; const aliased = TZ_ALIAS[tz] || tz; const KNOWN = ["America/Toronto","America/Chicago","America/Denver","America/Los_Angeles","America/Halifax","America/Winnipeg","America/Edmonton","America/Sao_Paulo","America/Mexico_City","Europe/Paris","Australia/Sydney"]; return KNOWN.includes(aliased) ? aliased : "America/Toronto"; } catch(e) { return "America/Toronto"; } })() });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [legalModal, setLegalModal] = useState(null); // "terms" | "privacy" | null

  // Step 4 — background completion
  const [userId, setUserId] = useState(null); // eslint-disable-line no-unused-vars
  const [sessionToken, setSessionToken] = useState(null); // stored to localStorage ONLY after finalize
  const [completeError, setCompleteError] = useState(null);
  const [completeLoading, setCompleteLoading] = useState(false); // eslint-disable-line no-unused-vars

  // Step 5 — finalize
  const [finalizeLoading, setFinalizeLoading] = useState(false);
  const [finalizeError, setFinalizeError] = useState(null);

  // LinkedIn connect
  const [linkedinConnecting, setLinkedinConnecting] = useState(false);
  const [linkedinError, setLinkedinError] = useState(null);
  const [manualChecking, setManualChecking] = useState(false);
  const [manualError, setManualError] = useState(null);

  const [busyBee, setBusyBee] = useState(false);

  // Restore LinkedIn data after OAuth redirect
  useEffect(() => {
    const saved = sessionStorage.getItem("nectar_li_connected");
    if (!saved) return;
    sessionStorage.removeItem("nectar_li_connected");
    try {
      const data = JSON.parse(saved);
      if (data.connected) {
        if (data.linkedin_name) {
          setLinkedinName(data.linkedin_name);
          setFirstName(data.linkedin_name.split(" ")[0]);
        }
        if (data.linkedin_profile_image) setLinkedinProfileImage(data.linkedin_profile_image);
        setStep(2);
      }
    } catch {}
  }, []); // eslint-disable-line

  useEffect(() => {
    const handler = () => setBusyBee(true);
    window.addEventListener("nectar:busy-bee", handler);
    return () => window.removeEventListener("nectar:busy-bee", handler);
  }, []);

  // Trigger background post generation when step 4 mounts
  useEffect(() => {
    if (step !== 4) return;
    runOnboardComplete();
  }, [step]); // eslint-disable-line

  // ── Topic helpers ──────────────────────────────────────────────────────────
  // topicSelection is the source of truth for WHAT is selected
  // selectedTopicIds tracks ORDER only
  // totalSelections: whole category = 1, each individual subtopic = 1
  const totalSelections = Object.values(topicSelection).reduce((sum, val) => {
    if (val === "all") return sum + 1;
    if (Array.isArray(val)) return sum + val.length;
    return sum;
  }, 0);

  const toggleTopic = id => {
    setTopicWarning(false);
    if (topicSelection[id] !== undefined) {
      const { [id]: _, ...rest } = topicSelection;
      setTopicSelection(rest);
      setSelectedTopicIds(p => p.filter(t => t !== id));
    } else {
      if (totalSelections >= 5) { setTopicWarning(true); return; }
      setTopicSelection(prev => ({ ...prev, [id]: "all" }));
      setSelectedTopicIds(p => {
        if (p.includes(id)) return p;
        return [...p, id];
      });
    }
  };

  const toggleSubtopic = (topicId, label) => {
    const cur = topicSelection[topicId];
    const isRemoving = Array.isArray(cur) && cur.includes(label);

    // Cap check on every ADD — not just new categories
    if (!isRemoving && totalSelections >= 5) {
      setTopicWarning(true);
      return;
    }
    setTopicWarning(false);

    // Add to order list if new category
    if (cur === undefined) {
      setSelectedTopicIds(p => p.includes(topicId) ? p : [...p, topicId]);
    }

    // Update selection
    setTopicSelection(prev => {
      const curVal = prev[topicId];
      const curArr = curVal === "all" ? [] : (Array.isArray(curVal) ? curVal : []);
      const newArr = curArr.includes(label) ? curArr.filter(l => l !== label) : [...curArr, label];
      if (newArr.length === 0) {
        const { [topicId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [topicId]: newArr };
    });

    // Remove from order list if last subtopic unchecked
    if (isRemoving && Array.isArray(cur) && cur.length === 1) {
      setSelectedTopicIds(p => p.filter(id => id !== topicId));
    }
  };

  // ── LinkedIn connect — NO onboard/start before OAuth ──────────────────────
  const handleLinkedInConnect = () => {
    setLinkedinConnecting(true);
    setLinkedinError(null);
    try {
      // Generate a temp UUID for CSRF nonce (no DB user created yet)
      const tempId = typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem("nectar_li_onboarding", "1");
      sessionStorage.removeItem("nectar_li_user_id"); // clear old ghost user keys
      window.location.href = `${API_BASE}/app/api/auth/linkedin/connect?temp_id=${tempId}`;
    } catch (err) {
      setLinkedinError("Something went wrong. Try again.");
      setLinkedinConnecting(false);
    }
  };

  // ── Manual continue — check Flask session for pending LinkedIn data ────────
  const handleManualContinue = async () => {
    setManualChecking(true);
    setManualError(null);
    try {
      const res = await fetch(`${API_BASE}/app/api/onboard/linkedin-session`, { credentials: "include" });
      const data = res.ok ? await res.json() : {};
      if (data.connected) {
        if (data.linkedin_name) {
          setLinkedinName(data.linkedin_name);
          setFirstName(data.linkedin_name.split(" ")[0]);
        }
        if (data.linkedin_profile_image) setLinkedinProfileImage(data.linkedin_profile_image);
        setStep(2);
      } else {
        setManualError("LinkedIn not connected yet. Please click 'Connect LinkedIn' above first.");
      }
    } catch {
      setManualError("Could not check connection status. Please try again.");
    }
    setManualChecking(false);
  };

  // ── onboard/complete (Step 4 → creates DB row) ────────────────────────────
  const runOnboardComplete = async () => {
    setCompleteLoading(true);
    setCompleteError(null);
    try {
      const res = await fetch(`${API_BASE}/app/api/onboard/complete`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tone: tone || "professional",
          mbti_type: mbtiType || null,
          topics: selectedTopicIds,
          topic_selection: topicSelection,
          onboard_token: sessionStorage.getItem("nectar_onboard_token") || undefined,
          ...(userProfession ? { profession: userProfession } : {}),
          ...(userJurisdiction ? { jurisdiction: userJurisdiction } : {}),
          ...(selectedPracticeAreas.length > 0 ? { practice_areas: selectedPracticeAreas } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.user_id) {
        setCompleteError(data.error || "Something went wrong. Please try again.");
        setCompleteLoading(false);
        return;
      }
      setUserId(data.user_id);
      setSessionToken(data.session_token); // NOT saved to localStorage yet
      // Kick off background post generation (fire-and-forget)
      fetch(`${API_BASE}/app/api/user/generate-posts`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Session-Token": data.session_token,
        },
      }).catch(() => {});
      setCompleteLoading(false);
      setStep(5);
    } catch (err) {
      setCompleteError("Connection error. Please check your internet and try again.");
      setCompleteLoading(false);
    }
  };

  // ── onboard/finalize (Step 5 → save email + prefs → dashboard) ────────────
  const handleFinalize = async () => {
    setFinalizeLoading(true);
    setFinalizeError(null);
    try {
      const res = await fetch(`${API_BASE}/app/api/onboard/finalize`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Session-Token": sessionToken,
        },
        body: JSON.stringify({
          first_name: firstName.trim(),
          email,
          posting_mode: mode || "review",
          review_window_timezone: schedule.timezone,
          ...(mode === "review" && schedule.day !== null && schedule.hour !== null ? {
            review_window_day: DAYS_OF_WEEK[schedule.day],
            review_window_time: schedule.hour === 12 ? "12:00pm"
              : schedule.hour < 12 ? `${schedule.hour}:00am`
              : `${schedule.hour - 12}:00pm`,
          } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setFinalizeError(data.error || "Failed to save your details. Please try again.");
        setFinalizeLoading(false);
        return;
      }
      // Only now save session token to localStorage
      if (sessionToken) {
        localStorage.setItem("nectar_st", sessionToken);
        localStorage.setItem("nectar_token_created", String(Date.now()));
      }
      setFinalizeLoading(false);
      if (onComplete) onComplete();
      else window.location.hash = "#/dashboard";
    } catch (err) {
      setFinalizeError("Connection error. Please try again.");
      setFinalizeLoading(false);
    }
  };

  // ── Progress bar helper ────────────────────────────────────────────────────
  // Map step (1-5) to progress bar index (0-3)
  // step 1→0, step 2→1, step 3→2, step 5→3
  const PROGRESS_LABELS = proQualified ? PROGRESS_LABELS_PRO : PROGRESS_LABELS_STANDARD;
  const progressIndex = proQualified
    ? (step === 1 ? 0 : step === 2 ? 1 : step === STEP_PROFESSION ? 2 : step === STEP_PRACTICE_AREAS ? 3 : step === 5 ? 4 : -1)
    : (step === 1 ? 0 : step === 2 ? 1 : step === STEP_PROFESSION ? 2 : step === 3 ? 2 : step === 5 ? 3 : -1);
  const showProgress = step >= 1 && step !== 4;

  const tz = TIMEZONES.find(t => t.id === schedule.timezone);
  const scheduleComplete = schedule.day !== null && schedule.hour !== null;
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const emailsMatch = emailValid && email === emailConfirm;
  const canFinalize = firstName.trim() && emailsMatch && mode && termsAccepted && ageConfirmed &&
    (mode !== "review" || scheduleComplete);

  const voiceReady = !!tone || !!mbtiType;

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ background: BG, minHeight: "100vh", color: "#1A1A1A", fontFamily: "'Playfair Display', Georgia, serif" }}>
      {busyBee && <BusyBee onRetry={() => setBusyBee(false)} />}
      {legalModal && <LegalModal type={legalModal} onClose={() => setLegalModal(null)} />}
      <style>{`
        :root{--blue:#0077B5;--blue-dk:#005885;--blue-lt:#EBF5FB;--bg:#F3F2EF;--card:#FFFFFF;--border:#E1E9EE;--text:#1A1A1A;--muted:#666666;--green:#057642;--green-lt:#F0FAF4;}
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #F3F2EF; -webkit-font-smoothing: antialiased; font-family: -apple-system,'Segoe UI',Helvetica,sans-serif; color: #1A1A1A; }
        .mono { font-family: -apple-system,'Segoe UI',Helvetica,sans-serif; font-weight: 400; }
        @keyframes fadeUp { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:translateY(0); } }
        .fade-up { animation: fadeUp 0.45s ease forwards; }
        @keyframes pulse { 0%,100%{opacity:.3} 50%{opacity:.65} }
        .pulsing { animation: pulse 1.5s ease infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; display: inline-block; }
        @keyframes beeSpin { 0%{transform:translateY(0)} 50%{transform:translateY(-6px)} 100%{transform:translateY(0)} }
        .bee-bounce { animation: beeSpin 1.2s ease-in-out infinite; display: inline-block; }
        input { outline: none; font-family: -apple-system,'Segoe UI',Helvetica,sans-serif; background: #fff; border-radius: 4px; }
        input::placeholder { color: #999; }
        .selcard { transition: all 0.18s; cursor: pointer; }
        .selcard:hover { border-color: #0077B5 !important; }
        em { font-style: italic; }
        @media (max-width: 480px) { .onb-btn-row { flex-direction: column !important; } }
        @media (max-width: 480px) { .onb-profession-grid { grid-template-columns: 1fr !important; } }
        @media (max-width: 600px) { .mbti-groups-grid { grid-template-columns: 1fr !important; } }
      `}</style>

      {/* ── Top nav ── */}
      <div style={{ borderBottom: `1px solid ${BORDER}`, padding: "0 48px", height: 72, display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, background: "#FFFFFF", zIndex: 100, boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
        <BrandLogo size="md" showText={false} />
        {showProgress && (
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ display: "flex", gap: 4 }}>
              {PROGRESS_LABELS.map((_, i) => (
                <div key={i} style={{ width: 28, height: 2, borderRadius: 2, background: i <= progressIndex ? GOLD : "#E1E9EE", transition: "background 0.4s" }} />
              ))}
            </div>
            <div className="mono" style={{ fontSize: 12, color: "#666666" }}>
              {PROGRESS_LABELS[progressIndex] || ""}
            </div>
          </div>
        )}
      </div>

      <div style={{ maxWidth: 580, margin: "0 auto", padding: "48px 24px 60px" }}>

        {/* ═══════════════════════════════════════════════════════════
            STEP 0 — LANDING
        ═══════════════════════════════════════════════════════════ */}
        {step === 0 && (
          <div className="fade-up">
            <div className="mono" style={{ fontSize: 13, color: GOLD_DIM, marginBottom: 20, letterSpacing: "0.05em" }}>
              LinkedIn content · automated · in your voice
            </div>
            <div style={{ fontSize: 54, lineHeight: 1.1, letterSpacing: "-0.025em", marginBottom: 24 }}>
              Opportunities don't go to the most qualified.<br />They go to whoever<br /><em style={{ color: GOLD }}>stayed top of mind.</em>
            </div>
            <div style={{ fontSize: 18, color: "#666", lineHeight: 1.8, marginBottom: 48, maxWidth: 460 }}>
              BrandHives finds the best content in your industry, rewrites it in your exact voice, and posts to LinkedIn for you — 5 times a week, automatically.
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1, background: BORDER, borderRadius: 14, overflow: "hidden", marginBottom: 40 }}>
              {[["Auto","posts weekly"],["2 min","to set up"],["100%","your voice"]].map(([s,l]) => (
                <div key={l} style={{ background: CARD, padding: "24px 16px", textAlign: "center" }}>
                  <div style={{ fontSize: 34, color: GOLD, marginBottom: 6 }}>{s}</div>
                  <div className="mono" style={{ fontSize: 12, color: "#666666" }}>{l}</div>
                </div>
              ))}
            </div>

            <button onClick={() => setStep(1)} style={{ width: "100%", background: GOLD, color: BG, border: "none", borderRadius: 11, padding: "18px", fontSize: 16, cursor: "pointer", fontFamily: "'Playfair Display',Georgia,serif", marginBottom: 12, letterSpacing: "0.01em", minHeight: 44 }}>
              Start my 30-day free trial →
            </button>
            <div className="mono" style={{ textAlign: "center", fontSize: 12, color: "#999999", marginBottom: 56 }}>
              No charge today · No credit card required · Cancel anytime
            </div>

            <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 44 }}>
              <div className="mono" style={{ fontSize: 12, letterSpacing: "0.2em", color: "#999999", textTransform: "uppercase", marginBottom: 32 }}>How it works</div>
              {[
                ["01","Connect your LinkedIn","One click. Secure OAuth. We never store your password."],
                ["02","Tell us your voice & topics","Your MBTI type + 3–5 topics. 2 minutes."],
                ["03","Review weekly or set it to autopilot","Your call. Switch modes anytime in Settings."],
                ["04","Posts every week, in your voice","We pick the best content and write it for you."],
              ].map(([n, t, d]) => (
                <div key={n} style={{ display: "flex", gap: 20, marginBottom: 28 }}>
                  <div className="mono" style={{ fontSize: 13, color: GOLD_DIM, paddingTop: 2, minWidth: 24 }}>{n}</div>
                  <div>
                    <div style={{ fontSize: 18, marginBottom: 4 }}>{t}</div>
                    <div className="mono" style={{ fontSize: 13, color: "#666666", lineHeight: 1.6 }}>{d}</div>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => setStep(1)} style={{ width: "100%", background: "transparent", color: GOLD, border: `1px solid ${GOLD_DIM}`, borderRadius: 11, padding: "16px", fontSize: 16, cursor: "pointer", fontFamily: "'Playfair Display',Georgia,serif", marginTop: 16, minHeight: 44 }}>
              Get started — free for 30 days
            </button>
            {onSignIn && (
              <div className="mono" style={{ textAlign: "center", fontSize: 12, color: "#999999", marginTop: 20 }}>
                Already have an account?{" "}
                <span onClick={onSignIn} style={{ color: GOLD_DIM, cursor: "pointer" }}>Sign in →</span>
              </div>
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════
            STEP 1 — LINKEDIN CONNECT
        ═══════════════════════════════════════════════════════════ */}
        {step === 1 && (
          <div className="fade-up">
            <div style={{ fontSize: 44, lineHeight: 1.2, marginBottom: 6 }}>Connect your<br /><em style={{ color: GOLD }}>LinkedIn.</em></div>
            <div className="mono" style={{ fontSize: 16, color: "#666666", marginBottom: 36, lineHeight: 1.6 }}>
              We'll post to your LinkedIn automatically — in your voice, matched to your topics.
            </div>

            <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", padding: "24px", marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
                <div style={{ width: 48, height: 48, background: "#0077b5", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <span style={{ color: "#fff", fontSize: 26, fontWeight: "bold", fontFamily: "sans-serif" }}>in</span>
                </div>
                <div>
                  <div style={{ fontSize: 18 }}>Connect LinkedIn</div>
                  <div className="mono" style={{ fontSize: 12, color: "#666666" }}>Secure OAuth · Direct LinkedIn connection · We never store your password</div>
                </div>
              </div>
              {[
                "Post content on your behalf",
                "Read your profile to personalize posts",
                "Access post analytics",
              ].map(p => (
                <div key={p} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <div style={{ width: 5, height: 5, borderRadius: "50%", background: GOLD_DIM }} />
                  <div className="mono" style={{ fontSize: 12, color: "#666666" }}>{p}</div>
                </div>
              ))}
            </div>

            {linkedinConnecting ? (
              <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 12, padding: "28px 24px", textAlign: "center", marginBottom: 20 }}>
                <div className="mono pulsing" style={{ fontSize: 13, color: GOLD_DIM }}>Redirecting to LinkedIn...</div>
              </div>
            ) : (
              <>
                <button
                  onClick={handleLinkedInConnect}
                  style={{ width: "100%", background: "#0077b5", color: "#fff", border: "none", borderRadius: 10, padding: "17px", fontSize: 16, cursor: "pointer", fontFamily: "'Playfair Display',Georgia,serif", marginBottom: 12, minHeight: 44 }}
                >
                  Connect LinkedIn →
                </button>
                {linkedinError && (
                  <div className="mono" style={{ textAlign: "center", fontSize: 12, color: "#cc0000", marginBottom: 8 }}>
                    {linkedinError}
                  </div>
                )}
                <div className="mono" style={{ textAlign: "center", fontSize: 12, color: "#999999", marginBottom: 20 }}>
                  You'll be redirected to LinkedIn · Comes back here automatically after you authorize
                </div>
                <button
                  onClick={handleManualContinue}
                  disabled={manualChecking}
                  style={{ width: "100%", background: "transparent", color: "#666", border: "1px solid #ccc", borderRadius: 10, padding: "13px", fontSize: 14, cursor: "pointer", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", minHeight: 44 }}
                >
                  {manualChecking ? "Checking..." : "Already connected? Continue →"}
                </button>
                {manualError && (
                  <div className="mono" style={{ textAlign: "center", fontSize: 12, color: "#cc0000", marginTop: 8 }}>
                    {manualError}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════
            STEP 2 — VOICE
        ═══════════════════════════════════════════════════════════ */}
        {step === 2 && (
          <div className="fade-up">
            <div className="mono" style={{ fontSize: 13, color: "#999999", marginBottom: 8 }}>Step 1 of 3</div>
            <div style={{ fontSize: 36, lineHeight: 1.2, marginBottom: 6 }}>
              Pick your voice{linkedinName ? `, ${linkedinName.split(" ")[0]}` : ""} 👋
            </div>
            <div className="mono" style={{ fontSize: 15, color: "#666666", marginBottom: 24, lineHeight: 1.6 }}>
              This shapes every post we write for you. You can change it anytime.
            </div>

            {/* Tone cards — always visible */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 28 }}>
              {TONES.map(t => (
                <div
                  key={t.id}
                  onClick={() => setTone(t.id)}
                  className="selcard"
                  style={{ border: `1px solid ${tone === t.id ? GOLD : BORDER}`, borderRadius: 10, padding: "16px 18px", cursor: "pointer", background: tone === t.id ? GOLD_DARK : "transparent", transition: "all 0.2s" }}
                >
                  <div style={{ fontSize: 16, fontWeight: 600, color: tone === t.id ? GOLD_DIM : "#1A1A1A", marginBottom: 4 }}>{t.label}</div>
                  <div className="mono" style={{ fontSize: 13, color: "#555555", lineHeight: 1.5 }}>{t.fullDesc}</div>
                </div>
              ))}
            </div>

            {/* MBTI section */}
            <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 20, marginBottom: 20 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#1A1A1A", marginBottom: 14 }}>Know your Myers-Briggs type?</div>

              {/* Pick your type — expandable */}
              <div style={{ marginBottom: 8 }}>
                <div
                  onClick={() => { setVoicePath(voicePath === "dropdown" ? null : "dropdown"); setQuizStep(0); setQuizAnswers({}); }}
                  className="selcard"
                  style={{ border: `1.5px solid ${voicePath === "dropdown" ? GOLD : BORDER}`, borderRadius: 10, padding: "14px 18px", background: voicePath === "dropdown" ? GOLD_DARK : "transparent", display: "flex", gap: 14, alignItems: "center", cursor: "pointer", transition: "all 0.15s" }}
                >
                  <div style={{ fontSize: 22 }}>⚡</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: voicePath === "dropdown" ? GOLD_DIM : "#1A1A1A" }}>Pick your type</div>
                    <div className="mono" style={{ fontSize: 12, color: "#666666", marginTop: 2 }}>Choose from the 16 types — for when you already know yours.</div>
                  </div>
                  <span style={{ fontSize: 13, color: "#BBBBBB", transform: voicePath === "dropdown" ? "rotate(180deg)" : "none", display: "inline-block", transition: "transform 0.2s" }}>▾</span>
                </div>
                {voicePath === "dropdown" && (
                  <div className="fade-up" style={{ marginTop: 10, marginBottom: 8 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 12 }}>
                      {MBTI_TYPES.map(({ type, label }) => (
                        <button key={type} onClick={() => setMbtiType(type)}
                          style={{ padding: "10px 6px", borderRadius: 8, border: `1.5px solid ${mbtiType === type ? GOLD : BORDER}`, background: mbtiType === type ? GOLD_DARK : CARD, color: mbtiType === type ? GOLD_DIM : "#1A1A1A", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", lineHeight: 1.3, textAlign: "center" }}>
                          <div>{type}</div>
                          <div style={{ fontSize: 10, fontWeight: 400, color: mbtiType === type ? GOLD_DIM : "#888", marginTop: 2 }}>{label}</div>
                        </button>
                      ))}
                    </div>
                    {mbtiType && (
                      <div className="fade-up" style={{ border: `1px solid ${BORDER}`, borderRadius: 8, padding: "12px 16px", background: GOLD_DARK }}>
                        <div className="mono" style={{ fontSize: 13, color: GOLD_DIM, fontWeight: 700 }}>{mbtiType} — {MBTI_TYPES.find(m => m.type === mbtiType)?.label}</div>
                        <div className="mono" style={{ fontSize: 12, color: "#555555", marginTop: 4 }}>{MBTI_DESCS[mbtiType]}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Take the quiz — expandable */}
              <div>
                <div
                  onClick={() => { setVoicePath(voicePath === "groups" ? null : "groups"); setQuizStep(0); setQuizAnswers({}); }}
                  className="selcard"
                  style={{ border: `1.5px solid ${voicePath === "groups" ? GOLD : BORDER}`, borderRadius: 10, padding: "14px 18px", background: voicePath === "groups" ? GOLD_DARK : "transparent", display: "flex", gap: 14, alignItems: "center", cursor: "pointer", transition: "all 0.15s" }}
                >
                  <div style={{ fontSize: 22 }}>🧭</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: voicePath === "groups" ? GOLD_DIM : "#1A1A1A" }}>Take the quiz</div>
                    <div className="mono" style={{ fontSize: 12, color: "#666666", marginTop: 2 }}>4 questions, 60 seconds — we'll find your type.</div>
                  </div>
                  <span style={{ fontSize: 13, color: "#BBBBBB", transform: voicePath === "groups" ? "rotate(180deg)" : "none", display: "inline-block", transition: "transform 0.2s" }}>▾</span>
                </div>
                {voicePath === "groups" && (() => {
                  const QUIZ = [
                    { key: "ei", question: "Where do you get your energy?",
                      options: [{ val: "E", emoji: "🤝", label: "From people", sub: "Energized by socializing and collaboration" },
                                 { val: "I", emoji: "🧘", label: "From alone time", sub: "Recharge by reflecting and working solo" }] },
                    { key: "sn", question: "How do you take in information?",
                      options: [{ val: "S", emoji: "🔍", label: "Facts & details", sub: "Trust what you can observe and measure" },
                                 { val: "N", emoji: "💡", label: "Patterns & ideas", sub: "Look for connections and big-picture meaning" }] },
                    { key: "tf", question: "How do you make decisions?",
                      options: [{ val: "T", emoji: "⚖️", label: "Logic & objectivity", sub: "Prioritize consistency and what makes sense" },
                                 { val: "F", emoji: "❤️", label: "Values & empathy", sub: "Prioritize people and what feels right" }] },
                    { key: "jp", question: "How do you approach life?",
                      options: [{ val: "J", emoji: "📋", label: "Planned & structured", sub: "Prefer clear goals and organized routines" },
                                 { val: "P", emoji: "🌊", label: "Flexible & spontaneous", sub: "Prefer to adapt and keep options open" }] },
                  ];
                  const answered = Object.keys(quizAnswers).length;
                  const allDone = answered === 4;
                  if (allDone && quizStep === 4) {
                    const result = (quizAnswers.ei||"E")+(quizAnswers.sn||"S")+(quizAnswers.tf||"T")+(quizAnswers.jp||"J");
                    const typeInfo = MBTI_TYPES.find(m => m.type === result);
                    return (
                      <div className="fade-up" style={{ marginTop: 10, marginBottom: 8 }}>
                        <div style={{ border: `2px solid ${GOLD}`, borderRadius: 12, padding: "20px 18px", background: GOLD_DARK, textAlign: "center", marginBottom: 12 }}>
                          <div style={{ fontSize: 32, fontWeight: 700, color: GOLD_DIM, fontFamily: "'Playfair Display',Georgia,serif", marginBottom: 4 }}>{result}</div>
                          <div style={{ fontSize: 16, fontWeight: 600, color: "#1A1A1A", marginBottom: 8 }}>The {typeInfo?.label}</div>
                          <div className="mono" style={{ fontSize: 13, color: "#555555", lineHeight: 1.6 }}>{MBTI_DESCS[result]}</div>
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button onClick={() => { setQuizStep(0); setQuizAnswers({}); setMbtiType(null); }}
                            style={{ flex: 1, padding: "10px", background: "transparent", color: "#666", border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                            Retake quiz
                          </button>
                          <button onClick={() => setMbtiType(result)}
                            style={{ flex: 2, padding: "10px", background: GOLD, color: BG, border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                            {mbtiType === result ? "✓ Saved as " + result : "Use " + result}
                          </button>
                        </div>
                      </div>
                    );
                  }
                  const q = QUIZ[Math.min(quizStep, 3)];
                  return (
                    <div className="fade-up" style={{ marginTop: 10, marginBottom: 8 }}>
                      <div style={{ display: "flex", gap: 6, marginBottom: 16, justifyContent: "center" }}>
                        {QUIZ.map((_, i) => (
                          <div key={i} style={{ width: 28, height: 4, borderRadius: 2, background: i < answered ? GOLD : i === quizStep ? GOLD_DIM : BORDER, transition: "background 0.2s" }} />
                        ))}
                      </div>
                      <div className="mono" style={{ fontSize: 12, color: "#999", marginBottom: 6 }}>Question {quizStep + 1} of 4</div>
                      <div style={{ fontSize: 18, fontWeight: 600, color: "#1A1A1A", marginBottom: 14, lineHeight: 1.4 }}>{q.question}</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {q.options.map(opt => (
                          <div key={opt.val}
                            className="selcard"
                            onClick={() => {
                              const next = { ...quizAnswers, [q.key]: opt.val };
                              setQuizAnswers(next);
                              const nextStep = quizStep + 1;
                              setQuizStep(nextStep === 4 ? 4 : nextStep);
                            }}
                            style={{ border: `1.5px solid ${quizAnswers[q.key] === opt.val ? GOLD : BORDER}`, borderRadius: 10, padding: "14px 16px", background: quizAnswers[q.key] === opt.val ? GOLD_DARK : "transparent", display: "flex", gap: 14, alignItems: "center", cursor: "pointer", transition: "all 0.15s" }}>
                            <div style={{ fontSize: 24 }}>{opt.emoji}</div>
                            <div>
                              <div style={{ fontSize: 15, fontWeight: 600, color: quizAnswers[q.key] === opt.val ? GOLD_DIM : "#1A1A1A" }}>{opt.label}</div>
                              <div className="mono" style={{ fontSize: 12, color: "#666", marginTop: 2 }}>{opt.sub}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                      {quizStep > 0 && (
                        <button onClick={() => setQuizStep(s => Math.max(0, s - 1))}
                          style={{ marginTop: 12, background: "transparent", color: "#666", border: "none", fontSize: 13, cursor: "pointer", padding: "4px 0" }}>
                          ← Back
                        </button>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>

            <div className="onb-btn-row" style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button onClick={() => setStep(1)} style={{ background: "transparent", color: "#666666", border: `1px solid ${BORDER}`, borderRadius: 9, padding: "14px 22px", fontSize: 15, cursor: "pointer", minHeight: 44 }}>← Back</button>
              <button
                onClick={() => { if (voiceReady) setStep(proEnabled ? STEP_PROFESSION : 3); }}
                disabled={!voiceReady}
                style={{ flex: 1, background: voiceReady ? GOLD : GOLD_DARK, color: voiceReady ? BG : "#999999", border: "none", borderRadius: 9, padding: "15px", fontSize: 16, cursor: voiceReady ? "pointer" : "not-allowed", fontFamily: "'Playfair Display',Georgia,serif", transition: "all 0.2s", minHeight: 44 }}
              >
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════
            STEP 3 — TOPICS
        ═══════════════════════════════════════════════════════════ */}
        {step === 3 && (
          <div className="fade-up">
            {proEnabled && userProfession && !proQualified && (
              <div className="mono" style={{ fontSize: 14, color: "#666", background: "#F9FAFB", border: `1px solid ${BORDER}`, borderRadius: 8, padding: "12px 16px", marginBottom: 16, lineHeight: 1.6 }}>
                We're still building the news feed for your field. In the meantime, pick the topics you want to post about.
              </div>
            )}
            <div style={{ fontSize: 44, lineHeight: 1.2, marginBottom: 6 }}>What do you want<br /><em>to talk about?</em></div>
            <div className="mono" style={{ fontSize: 15, color: "#666666", marginBottom: 4 }}>Choose 3–5 topics · select in priority order</div>
            {topicWarning && (
              <div className="mono" style={{ fontSize: 12, color: "#C05050", marginBottom: 8 }}>Maximum 5 topics — remove one before adding another.</div>
            )}
            {totalSelections > 0 && totalSelections < 3 && (
              <div className="mono" style={{ fontSize: 12, color: "#999", marginBottom: 8 }}>Select {3 - totalSelections} more topic{3 - totalSelections !== 1 ? "s" : ""} to continue.</div>
            )}
            <div style={{ background: "#F3F2EF", border: `1px solid ${BORDER}`, borderRadius: 8, padding: "10px 14px", marginBottom: 16 }}>
              <div className="mono" style={{ fontSize: 12, color: "#999999", lineHeight: 1.6 }}>
                ✦ Topics 1–3 are your primary feed &nbsp;·&nbsp; Topics 4–5 used as alternates when available
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {CATEGORIES.map(c => {
                const sel = topicSelection[c.id] !== undefined;
                const expanded = expandedTopic === c.id;
                const selVal = topicSelection[c.id];
                const selSubs = sel && selVal !== "all" ? selVal : [];
                return (
                  <div key={c.id} style={{ border: `1px solid ${sel ? GOLD : BORDER}`, borderRadius: 10, overflow: "hidden", background: sel ? GOLD_DARK : "transparent", transition: "all 0.18s" }}>
                    <div style={{ display: "flex", alignItems: "center", padding: "13px 16px", cursor: "pointer", gap: 10 }} onClick={() => setExpandedTopic(expanded ? null : c.id)}>
                      <input type="checkbox" checked={sel} onClick={e => e.stopPropagation()} onChange={() => toggleTopic(c.id)} style={{ accentColor: GOLD, cursor: "pointer", width: 16, height: 16, flexShrink: 0 }} />
                      <span style={{ fontSize: 20 }}>{c.icon}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 16, lineHeight: 1.3 }}>{c.label}</div>
                        <div className="mono" style={{ fontSize: 12, color: "#666666" }}>{c.sub}</div>
                      </div>
                      <span style={{ fontSize: 13, color: "#BBBBBB", transform: expanded ? "rotate(180deg)" : "none", display: "inline-block", transition: "transform 0.2s" }}>▾</span>
                    </div>
                    {expanded && (
                      <div style={{ borderTop: `1px solid ${BORDER}`, padding: "12px 16px", background: "#FAFAFA" }} onClick={e => e.stopPropagation()}>
                        <div className="mono" style={{ fontSize: 11, color: "#999999", marginBottom: 8 }}>SELECT SUBTOPICS · leave all unchecked for the full topic feed</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 14px" }}>
                          {c.subtopics.map(st => {
                            const checked = selSubs.includes(st.label);
                            return (
                              <label key={st.label} style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer", fontSize: 14, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", color: checked ? GOLD_DIM : "#666666" }}>
                                <input type="checkbox" checked={checked} onChange={() => toggleSubtopic(c.id, st.label)} style={{ accentColor: GOLD, cursor: "pointer" }} />
                                {st.label}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mono" style={{ fontSize: 12, color: "#999999", lineHeight: 1.6, marginTop: 14, padding: "0 2px" }}>
              The order you pick shapes your content mix. You can reorder or swap topics any time in Settings → Topics.
            </div>
            <div className="mono" style={{ fontSize: 12, color: "#666666", lineHeight: 1.6, marginTop: 10, padding: "0 2px" }}>
              Don't see your niche? Add your favourite websites in RSS Feeds after you sign up. We're always adding new topics too. 🐝
            </div>
            <div className="onb-btn-row" style={{ display: "flex", gap: 10, marginTop: 14 }}>
              <button onClick={() => setStep(proEnabled ? STEP_PROFESSION : 2)} style={{ background: "transparent", color: "#666666", border: `1px solid ${BORDER}`, borderRadius: 9, padding: "14px 22px", fontSize: 15, cursor: "pointer", minHeight: 44 }}>← Back</button>
              <button
                onClick={() => { if (totalSelections >= 3) setStep(4); }}
                disabled={totalSelections < 3}
                style={{ flex: 1, background: totalSelections >= 3 ? GOLD : GOLD_DARK, color: totalSelections >= 3 ? BG : "#999999", border: "none", borderRadius: 9, padding: "14px", fontSize: 16, cursor: totalSelections >= 3 ? "pointer" : "not-allowed", fontFamily: "'Playfair Display',Georgia,serif", transition: "all 0.2s", minHeight: 44 }}
              >
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════
            STEP 6 — PROFESSION + JURISDICTION (Pro)
        ═══════════════════════════════════════════════════════════ */}
        {step === STEP_PROFESSION && (
          <div className="fade-up">
            <div style={{ fontSize: 44, lineHeight: 1.2, marginBottom: 6 }}>What's your<br /><em>field?</em></div>
            <div className="mono" style={{ fontSize: 15, color: "#666666", marginBottom: 20 }}>We'll bring you the developments that matter in your profession — written as your own informed commentary.</div>

            <div className="onb-profession-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
              {PROFESSIONS.map(p => (
                <div key={p.id}
                  onClick={() => setUserProfession(p.id)}
                  style={{ padding: "16px 14px", border: `1px solid ${userProfession === p.id ? GOLD : BORDER}`, borderRadius: 10, cursor: "pointer", background: userProfession === p.id ? "#EBF5FB" : "transparent", transition: "all 0.2s", fontSize: 15 }}>
                  {p.label}{userProfession === p.id && <span style={{ fontSize: 10, color: GOLD, marginLeft: 6 }}>✓</span>}
                </div>
              ))}
              <div
                onClick={() => setUserProfession("other")}
                style={{ padding: "16px 14px", border: `1px solid ${userProfession === "other" ? GOLD : BORDER}`, borderRadius: 10, cursor: "pointer", background: userProfession === "other" ? "#EBF5FB" : "transparent", transition: "all 0.2s", fontSize: 15, gridColumn: "1 / -1" }}>
                Something else
              </div>
            </div>

            {userProfession && (
              <>
                <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 8, marginTop: 16 }}>Where do you practice?</div>
                <div className="mono" style={{ fontSize: 13, color: "#666666", marginBottom: 12 }}>Rules and rulings differ by country.</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                  {JURISDICTIONS.map(j => (
                    <div key={j.id}
                      onClick={() => setUserJurisdiction(j.id)}
                      style={{ padding: "16px 14px", border: `1px solid ${userJurisdiction === j.id ? GOLD : BORDER}`, borderRadius: 10, cursor: "pointer", background: userJurisdiction === j.id ? "#EBF5FB" : "transparent", transition: "all 0.2s", fontSize: 15 }}>
                      {j.label}{userJurisdiction === j.id && <span style={{ fontSize: 10, color: GOLD, marginLeft: 6 }}>✓</span>}
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="onb-btn-row" style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button onClick={() => setStep(2)} style={{ background: "transparent", color: "#666666", border: `1px solid ${BORDER}`, borderRadius: 9, padding: "14px 22px", fontSize: 15, cursor: "pointer", minHeight: 44 }}>← Back</button>
              <button
                onClick={() => {
                  if (!proQualified) {
                    setStep(3); // Topics path
                  } else {
                    const areas = PRACTICE_AREA_MAP[userProfession] || [];
                    setSelectedPracticeAreas(areas.map(a => a.id));
                    setStep(STEP_PRACTICE_AREAS);
                  }
                }}
                disabled={!userProfession || !userJurisdiction}
                style={{ flex: 1, background: (userProfession && userJurisdiction) ? GOLD : GOLD_DARK, color: (userProfession && userJurisdiction) ? BG : "#999999", border: "none", borderRadius: 9, padding: "15px", fontSize: 16, cursor: (userProfession && userJurisdiction) ? "pointer" : "not-allowed", fontFamily: "'Playfair Display',Georgia,serif", transition: "all 0.2s", minHeight: 44 }}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════
            STEP 7 — PRACTICE AREAS (Pro qualified path)
        ═══════════════════════════════════════════════════════════ */}
        {step === STEP_PRACTICE_AREAS && (
          <div className="fade-up">
            <div style={{ fontSize: 44, lineHeight: 1.2, marginBottom: 6 }}>What do you<br /><em>cover?</em></div>
            <div className="mono" style={{ fontSize: 15, color: "#666666", marginBottom: 20 }}>Pick your practice areas. We'll prioritize content in these. Uncheck any you don't cover.</div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10, marginBottom: 20 }}>
              {(PRACTICE_AREA_MAP[userProfession] || []).map(pa => {
                const sel = selectedPracticeAreas.includes(pa.id);
                return (
                  <div key={pa.id}
                    onClick={() => setSelectedPracticeAreas(prev => sel ? prev.filter(x => x !== pa.id) : [...prev, pa.id])}
                    style={{ padding: "16px 18px", border: `1px solid ${sel ? GOLD : BORDER}`, borderRadius: 10, cursor: "pointer", background: sel ? "#EBF5FB" : "transparent", transition: "all 0.2s", fontSize: 15, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>{pa.label}</span>
                    {sel && <span style={{ color: GOLD, fontWeight: 700 }}>✓</span>}
                  </div>
                );
              })}
            </div>

            <div className="onb-btn-row" style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button onClick={() => setStep(STEP_PROFESSION)} style={{ background: "transparent", color: "#666666", border: `1px solid ${BORDER}`, borderRadius: 9, padding: "14px 22px", fontSize: 15, cursor: "pointer", minHeight: 44 }}>← Back</button>
              <button
                onClick={() => { if (selectedPracticeAreas.length > 0) setStep(4); }}
                disabled={selectedPracticeAreas.length === 0}
                style={{ flex: 1, background: selectedPracticeAreas.length > 0 ? GOLD : GOLD_DARK, color: selectedPracticeAreas.length > 0 ? BG : "#999999", border: "none", borderRadius: 9, padding: "15px", fontSize: 16, cursor: selectedPracticeAreas.length > 0 ? "pointer" : "not-allowed", fontFamily: "'Playfair Display',Georgia,serif", transition: "all 0.2s", minHeight: 44 }}
              >
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════
            STEP 4 — BACKGROUND GENERATION
        ═══════════════════════════════════════════════════════════ */}
        {step === 4 && (
          <div className="fade-up" style={{ textAlign: "center", paddingTop: 48 }}>
            {completeError ? (
              <>
                <div style={{ fontSize: 48, marginBottom: 24 }}>⚠️</div>
                <div style={{ fontSize: 32, lineHeight: 1.3, marginBottom: 16 }}>
                  Something went<br /><em style={{ color: "#C05050" }}>wrong.</em>
                </div>
                <div className="mono" style={{ fontSize: 15, color: "#666666", lineHeight: 1.7, marginBottom: 32 }}>
                  {completeError}
                </div>
                <button
                  onClick={runOnboardComplete}
                  style={{ background: GOLD, color: BG, border: "none", borderRadius: 10, padding: "16px 32px", fontSize: 16, cursor: "pointer", fontFamily: "'Playfair Display',Georgia,serif" }}
                >
                  Try again →
                </button>
              </>
            ) : (
              <>
                <div className="bee-bounce" style={{ fontSize: 56, marginBottom: 24 }}>🐝</div>
                <div style={{ fontSize: 36, lineHeight: 1.3, marginBottom: 16 }}>
                  Setting up your<br /><em style={{ color: GOLD }}>hive.</em>
                </div>
                <div className="mono pulsing" style={{ fontSize: 15, color: "#666666", lineHeight: 1.7, marginBottom: 8 }}>
                  Creating your account and prepping your first posts...
                </div>
                <div className="mono" style={{ fontSize: 12, color: "#999999" }}>
                  This takes just a moment.
                </div>
              </>
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════
            STEP 5 — LOGIN DETAILS + PREFERENCES
        ═══════════════════════════════════════════════════════════ */}
        {step === 5 && (
          <div className="fade-up">
            <div style={{ fontSize: 44, lineHeight: 1.2, marginBottom: 6 }}>Almost<br /><em style={{ color: GOLD }}>there.</em></div>
            <div className="mono" style={{ fontSize: 16, color: "#666666", marginBottom: 36, lineHeight: 1.6 }}>
              How should we reach you, and how do you want to run BrandHives?
            </div>

            {/* First name */}
            <div style={{ marginBottom: 24 }}>
              <div className="mono" style={{ fontSize: 12, color: "#666666", letterSpacing: "0.12em", marginBottom: 8 }}>WHAT SHOULD WE CALL YOU?</div>
              <input
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                placeholder="First name"
                type="text"
                style={{ width: "100%", background: CARD, border: `1px solid ${BORDER}`, borderRadius: 9, padding: "14px 16px", color: "#1A1A1A", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 16 }}
              />
            </div>

            {/* Email */}
            <div style={{ background: GOLD_DARK, border: `1px solid #B3D4E8`, borderRadius: 8, padding: "10px 14px", marginBottom: 16 }}>
              <div className="mono" style={{ fontSize: 12, color: "#005885", lineHeight: 1.6 }}>
                📧 This is your login — we'll send you a magic link each time you sign in. No password needed.
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div className="mono" style={{ fontSize: 12, color: "#666666", letterSpacing: "0.12em", marginBottom: 8 }}>EMAIL ADDRESS</div>
              <input
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="jane@company.com"
                type="email"
                style={{ width: "100%", background: CARD, border: `1px solid ${email && !emailValid ? "#C05050" : emailsMatch ? "#057642" : BORDER}`, borderRadius: 9, padding: "14px 16px", color: "#1A1A1A", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 16 }}
              />
              {email && !emailValid && (
                <div className="mono" style={{ fontSize: 12, color: "#C05050", marginTop: 4 }}>Please enter a valid email address.</div>
              )}
            </div>

            {/* Email confirm */}
            <div style={{ marginBottom: 28 }}>
              <div className="mono" style={{ fontSize: 12, color: "#666666", letterSpacing: "0.12em", marginBottom: 8 }}>CONFIRM YOUR EMAIL</div>
              <input
                value={emailConfirm}
                onChange={e => setEmailConfirm(e.target.value)}
                placeholder="Confirm email address"
                type="email"
                style={{ width: "100%", background: CARD, border: `1px solid ${emailConfirm && !emailsMatch ? "#C05050" : emailsMatch ? "#057642" : BORDER}`, borderRadius: 9, padding: "14px 16px", color: "#1A1A1A", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 16 }}
              />
              {emailConfirm && !emailsMatch && emailValid && (
                <div className="mono" style={{ fontSize: 12, color: "#C05050", marginTop: 4 }}>Emails don't match — please check and try again.</div>
              )}
            </div>

            {/* Posting mode */}
            <div style={{ fontSize: 22, marginBottom: 16 }}>
              How do you want to<br /><em>run this?</em>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 24 }}>
              {[
                { id: "review",   icon: "📬", title: "I want to edit\nbefore it posts",     desc: "Get a weekly email. Edit, swap, or remove posts before they go out." },
                { id: "autopost", icon: "🚀", title: "Set it and\nforget it",                 desc: "BrandHives automatically picks and posts the best articles every week." },
              ].map(m => (
                <div key={m.id} onClick={() => setMode(m.id)} className="selcard" style={{ border: `1px solid ${mode === m.id ? GOLD : BORDER}`, borderRadius: 12, padding: "20px 16px", background: mode === m.id ? GOLD_DARK : "transparent", textAlign: "center", transition: "all 0.2s" }}>
                  <div style={{ fontSize: 28, marginBottom: 10 }}>{m.icon}</div>
                  <div style={{ fontSize: 15, marginBottom: 8, whiteSpace: "pre-line" }}><em>{m.title}</em></div>
                  <div className="mono" style={{ fontSize: 12, color: "#666666", lineHeight: 1.65 }}>{m.desc}</div>
                  {mode === m.id && <div className="mono" style={{ marginTop: 10, fontSize: 12, color: GOLD }}>✓ selected</div>}
                </div>
              ))}
            </div>

            {/* Schedule (review mode) */}
            {mode === "review" && (
              <div className="fade-up" style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 12, padding: "24px", marginBottom: 20 }}>
                <div className="mono" style={{ fontSize: 12, color: "#666666", letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 20 }}>When should your preview arrive?</div>
                <div style={{ marginBottom: 18 }}>
                  <div className="mono" style={{ fontSize: 12, color: "#666666", marginBottom: 10 }}>Day</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 5 }}>
                    {DAYS_OF_WEEK.map((d, i) => (
                      <div key={d} onClick={() => setSchedule(p => ({ ...p, day: i }))} className="selcard" style={{ border: `1px solid ${schedule.day === i ? GOLD : BORDER}`, borderRadius: 7, padding: "10px 4px", textAlign: "center", background: schedule.day === i ? GOLD_DARK : "transparent" }}>
                        <div className="mono" style={{ fontSize: 12, color: schedule.day === i ? GOLD : "#666666" }}>{d.slice(0,3).toUpperCase()}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom: 18 }}>
                  <div className="mono" style={{ fontSize: 12, color: "#666666", marginBottom: 10 }}>Time</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 5 }}>
                    {HOURS.map(h => (
                      <div key={h} onClick={() => setSchedule(p => ({ ...p, hour: h }))} className="selcard" style={{ border: `1px solid ${schedule.hour === h ? GOLD : BORDER}`, borderRadius: 7, padding: "9px 4px", textAlign: "center", background: schedule.hour === h ? GOLD_DARK : "transparent" }}>
                        <div className="mono" style={{ fontSize: 12, color: schedule.hour === h ? GOLD : "#666666" }}>
                          {h < 12 ? `${h}am` : h === 12 ? "12pm" : `${h-12}pm`}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="mono" style={{ fontSize: 12, color: "#666666", marginBottom: 10 }}>Timezone</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5 }}>
                    {TIMEZONES.map(tz => (
                      <div key={tz.id} onClick={() => setSchedule(p => ({ ...p, timezone: tz.id }))} className="selcard" style={{ border: `1px solid ${schedule.timezone === tz.id ? GOLD : BORDER}`, borderRadius: 7, padding: "10px 12px", background: schedule.timezone === tz.id ? GOLD_DARK : "transparent", display: "flex", justifyContent: "space-between" }}>
                        <div className="mono" style={{ fontSize: 12, color: schedule.timezone === tz.id ? "#1A1A1A" : "#666666" }}>{tz.label}</div>
                        <div className="mono" style={{ fontSize: 12, color: schedule.timezone === tz.id ? GOLD_DIM : "#999999" }}>{tz.offset}</div>
                      </div>
                    ))}
                  </div>
                </div>
                {scheduleComplete && (
                  <div className="fade-up" style={{ marginTop: 20, borderTop: `1px solid ${BORDER}`, paddingTop: 16 }}>
                    <div className="mono" style={{ fontSize: 12, color: GOLD_DIM, marginBottom: 4 }}>YOUR REVIEW SCHEDULE</div>
                    <div style={{ fontSize: 22 }}>Every <em style={{ color: GOLD }}>{DAYS_OF_WEEK[schedule.day]}</em> at <em style={{ color: GOLD }}>{formatHour(schedule.hour)}</em></div>
                    <div className="mono" style={{ fontSize: 12, color: "#666666", marginTop: 3 }}>{tz?.label}</div>
                  </div>
                )}
              </div>
            )}

            {mode === "autopost" && (
              <div className="fade-up" style={{ background: "#F0FAF4", border: "1px solid #B7E4C7", borderRadius: 10, padding: "16px 20px", marginBottom: 24 }}>
                <div className="mono" style={{ fontSize: 12, color: GREEN, marginBottom: 4 }}>AUTOPILOT MODE</div>
                <div className="mono" style={{ fontSize: 13, color: GREEN, lineHeight: 1.65 }}>
                  BrandHives automatically selects and posts the best articles each week. No action needed. Switch to review mode anytime in Settings.
                </div>
              </div>
            )}

            {/* Timezone (autopost mode) */}
            {mode === "autopost" && (
              <div style={{ marginBottom: 24 }}>
                <div className="mono" style={{ fontSize: 12, color: "#666666", marginBottom: 10 }}>Your timezone</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5 }}>
                  {TIMEZONES.map(tz => (
                    <div key={tz.id} onClick={() => setSchedule(p => ({ ...p, timezone: tz.id }))} className="selcard" style={{ border: `1px solid ${schedule.timezone === tz.id ? GOLD : BORDER}`, borderRadius: 7, padding: "10px 12px", background: schedule.timezone === tz.id ? GOLD_DARK : "transparent", display: "flex", justifyContent: "space-between" }}>
                      <div className="mono" style={{ fontSize: 12, color: schedule.timezone === tz.id ? "#1A1A1A" : "#666666" }}>{tz.label}</div>
                      <div className="mono" style={{ fontSize: 12, color: schedule.timezone === tz.id ? GOLD_DIM : "#999999" }}>{tz.offset}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Consent checkboxes */}
            <label style={{ display: "flex", gap: 12, alignItems: "flex-start", cursor: "pointer", marginBottom: 10 }}>
              <input type="checkbox" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)} style={{ marginTop: 3, flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: "#555", lineHeight: 1.6 }}>
                I agree to BrandHives'{" "}
                <button type="button" onClick={e => { e.preventDefault(); setLegalModal("terms"); }} style={{ background: "none", border: "none", padding: 0, color: "#0077B5", cursor: "pointer", fontSize: 13, textDecoration: "underline", fontFamily: "inherit" }}>Terms of Use</button>
                {" "}and{" "}
                <button type="button" onClick={e => { e.preventDefault(); setLegalModal("privacy"); }} style={{ background: "none", border: "none", padding: 0, color: "#0077B5", cursor: "pointer", fontSize: 13, textDecoration: "underline", fontFamily: "inherit" }}>Privacy Policy</button>
                , and to receive account and service emails from BrandHives.
              </span>
            </label>
            <label style={{ display: "flex", gap: 12, alignItems: "flex-start", cursor: "pointer", marginBottom: 24 }}>
              <input type="checkbox" checked={ageConfirmed} onChange={e => setAgeConfirmed(e.target.checked)} style={{ marginTop: 3, flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: "#555", lineHeight: 1.6 }}>
                I confirm I am 16 years of age or older.
              </span>
            </label>

            {finalizeError && (
              <div style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 8, padding: "12px 16px", marginBottom: 16 }}>
                <div className="mono" style={{ fontSize: 13, color: "#C05050", lineHeight: 1.6 }}>{finalizeError}</div>
                <button onClick={handleFinalize} style={{ marginTop: 8, background: "transparent", color: "#C05050", border: "1px solid #FCA5A5", borderRadius: 6, padding: "6px 14px", fontSize: 12, cursor: "pointer", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
                  Try again
                </button>
              </div>
            )}

            <button
              onClick={handleFinalize}
              disabled={!canFinalize || finalizeLoading}
              style={{ width: "100%", background: canFinalize && !finalizeLoading ? GOLD : GOLD_DARK, color: canFinalize && !finalizeLoading ? BG : "#999999", border: "none", borderRadius: 10, padding: "17px", fontSize: 16, cursor: canFinalize && !finalizeLoading ? "pointer" : "not-allowed", fontFamily: "'Playfair Display',Georgia,serif", marginBottom: 12, transition: "all 0.2s", minHeight: 44 }}
            >
              {finalizeLoading ? <span>Setting up<span className="spin"> ◌</span></span> : "Go to my dashboard →"}
            </button>
            <div className="mono" style={{ textAlign: "center", fontSize: 12, color: "#999999" }}>
              Free 30-day trial · No credit card required · Cancel anytime
            </div>
          </div>
        )}

      </div>
      <div style={{ background: "#F3F2EF", borderTop: "1px solid #E1E9EE", padding: "16px 24px", textAlign: "center" }}>
        <div style={{ fontSize: 11, color: "#999999", lineHeight: 1.7, textAlign: "center" }}>AI by Claude · Payments secured by Stripe · Developed by BHIVE™ Labs</div>
        <div style={{ fontSize: 11, color: "#999999", textAlign: "center" }}>Trusted since 2011</div>
      </div>
    </div>
  );
}
